import React, { useState } from 'react';
import './RegistrationPage.css';
import { getErrorMessage } from '../utils/api';

interface PlayerRegistrationPageProps {
  onBackToLogin: () => void;
  onRegistrationSuccess: () => void;
}

const PlayerRegistrationPage: React.FC<PlayerRegistrationPageProps> = ({ onBackToLogin, onRegistrationSuccess }) => {
  // PHASE 1: Players can only register with these fields
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    whatsapp_number: '', // Single whatsapp field (player or parent)
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      // Also clear general error if all field errors are cleared
      setError(null);
    }
    if (name === 'last_name') {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // PHASE 1: Only validate allowed fields for players
    const username = formData.username?.trim() || '';
    const email = formData.email?.trim() || '';
    const password = formData.password?.trim() || '';
    const first_name = formData.first_name?.trim() || '';
    const last_name = formData.last_name?.trim() || '';
    const whatsapp_number = formData.whatsapp_number?.trim() || '';
    
    if (!username) {
      errors.username = 'Username is required';
    }
    if (!email) {
      errors.email = 'Email is required';
    }
    if (!password) {
      errors.password = 'Password is required';
    }
    if (!first_name) {
      errors.first_name = 'First name is required';
    }
    if (!last_name) {
      errors.last_name = 'Last name is required';
    }
    if (!whatsapp_number) {
      errors.whatsapp_number = 'WhatsApp number is required';
    }
    
    // Email validation
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (password) {
      if (password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
        errors.password = 'Password must contain both letters and numbers';
      }
    }
    
    // WhatsApp number basic validation (should contain digits)
    if (whatsapp_number && !/^[\d\s\-+()]+$/.test(whatsapp_number)) {
      errors.whatsapp_number = 'Please enter a valid WhatsApp number';
    }
    
    // Force React to update by using a new object
    const errorObj = { ...errors };
    setFieldErrors(errorObj);
    const hasErrors = Object.keys(errorObj).length > 0;
    
    if (hasErrors) {
      console.log('❌ Validation errors found:', errorObj);
      console.log('❌ Fields with errors:', Object.keys(errorObj));
      console.log('❌ Form data state:', formData);
    } else {
      console.log('✅ All validations passed');
    }
    
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 Form submit started at:', new Date().toISOString());
    console.log('🚀 Form data:', JSON.stringify(formData, null, 2));
    
    setError(null);
    setFieldErrors({}); // Clear previous errors
    
    // Validate form before submission
    console.log('🔍 Running validation...');
    const isValid = validateForm();
    console.log('🔍 Form validation result:', isValid);
    
    if (!isValid) {
      console.log('❌ Validation failed, preventing submission');
      const errorMsg = 'Please fix the errors in the form. Fields with errors are highlighted in red.';
      setError(errorMsg);
      alert('Validation Error: ' + errorMsg + '\n\nCheck console (F12) for details.');
      // Scroll to first error after a brief delay to ensure DOM is updated
      setTimeout(() => {
        const firstErrorField = document.querySelector('.field-error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (firstErrorField as HTMLElement).focus();
        }
      }, 100);
      return;
    }

    console.log('✅ Validation passed, submitting form...');
    setLoading(true);

    try {
      const { buildApiUrl } = require('../config/api');
      
      // Helper function to convert empty strings to null
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const toNullIfEmpty = (value: string | null | undefined): string | null => {
        if (!value || typeof value !== 'string') return null;
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
      };
      
      // PHASE 1: Only send allowed fields for player registration
      const requestBody = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim().toUpperCase(),
        whatsapp_number: formData.whatsapp_number.trim(),
      };
      
      const registerUrl = buildApiUrl('/auth/register/player');
      console.log('📤 Sending registration request to:', registerUrl);
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));
      
      let response: Response;
      let data: any;
      
      try {
        console.log('📡 About to send fetch request...');
        console.log('📡 URL:', registerUrl);
        console.log('📡 Request body:', JSON.stringify(requestBody, null, 2));
        
        response = await fetch(registerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        
        console.log('📡 Fetch completed, status:', response.status);
        
        console.log('📥 Response status:', response.status, response.statusText);
        console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

        // Try to get response as text first
        const responseText = await response.text();
        console.log('📥 Raw response text:', responseText);
        
        // Try to parse as JSON
        try {
          data = responseText ? JSON.parse(responseText) : {};
          console.log('📥 Parsed JSON response:', data);
        } catch (parseErr) {
          console.error('❌ Failed to parse as JSON:', parseErr);
          data = { detail: responseText || 'Unknown error', message: responseText || 'Unknown error' };
        }
      } catch (fetchErr: any) {
        console.error('❌ Fetch error:', fetchErr);
        setError('Network error: ' + (fetchErr.message || 'Failed to connect to server'));
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        alert(`Registration successful!\n\nUsername: ${data.username}\n\nNote: Additional profile information will be completed by your coach or administrator.`);
        onRegistrationSuccess();
      } else {
        console.error('❌ Registration failed');
        console.error('❌ Response status:', response.status, response.statusText);
        console.error('❌ Response data:', data);
        console.error('❌ Request that was sent:', requestBody);
        
        // If backend returns "Missing required fields", validate form to show which fields are missing
        const errorMessage = data.detail || data.message || 'Registration failed';
        console.error('❌ Error message:', errorMessage);
        
        if (errorMessage.includes('Missing required fields') || errorMessage.toLowerCase().includes('missing')) {
          console.error('❌ Backend says missing required fields - re-validating form');
          
          // Re-validate to show field errors - this will highlight missing fields
          const isValid = validateForm();
          console.error('❌ Re-validation result:', isValid);
          
          // Force DOM update by directly adding error classes to empty required fields
          const requiredFieldNames = ['username', 'email', 'password', 'first_name', 'last_name', 'whatsapp_number'];
          requiredFieldNames.forEach(fieldName => {
            const field = document.querySelector(`input[name="${fieldName}"], select[name="${fieldName}"]`) as HTMLElement;
            if (field) {
              const value = (field as HTMLInputElement).value?.trim() || '';
              if (!value) {
                field.classList.add('field-error');
                console.log(`❌ Added error class to field: ${fieldName}`);
              }
          }
          });
          
          if (!isValid) {
            setError('Please fill in all required fields marked in red below');
            // Scroll to first error
            setTimeout(() => {
              const firstErrorField = document.querySelector('.field-error') as HTMLElement;
              console.log('❌ First error field found:', firstErrorField);
              if (firstErrorField) {
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                firstErrorField.focus();
              } else {
                console.error('❌ No .field-error elements found in DOM');
              }
            }, 300);
          } else {
            // All fields validated on frontend but backend still says missing - show backend error
            setError('Registration failed: ' + errorMessage + '. Please check all required fields. All fields appear to be filled.');
            // Force re-validation with more strict checks
            validateForm();
          }
        } else {
          const finalErrorMessage = getErrorMessage(data, 'Registration failed: ' + errorMessage);
          console.error('❌ Setting error message:', finalErrorMessage);
          setError(finalErrorMessage);
          // Also show alert for visibility
          alert('Registration Error: ' + finalErrorMessage + '\n\nCheck the browser console (F12) for more details.');
        }
      }
    } catch (err: any) {
      console.error('❌ Unexpected error in handleSubmit:', err);
      console.error('❌ Error stack:', err?.stack);
      const errorMsg = err?.message || err?.toString() || 'Registration failed. Please try again.';
      setError(errorMsg);
      
      // Show detailed error in alert
      const detailedError = `Registration Error!\n\n${errorMsg}\n\nError type: ${err?.name || 'Unknown'}\n\nPlease check:\n1. All required fields are filled\n2. Browser console (F12) for details\n3. Network tab to see the request`;
      alert(detailedError);
      
      // Also try to show which fields might be missing
      validateForm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="registration-container">
        <h2>Player Registration</h2>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* Hidden dummy fields to prevent browser autocomplete */}
          <input type="text" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="password" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="email" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <div className="form-row">
            <div className="form-group">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={formData.username} 
                onChange={handleChange} 
                required 
                autoComplete="off" 
                id="player-username"
                className={fieldErrors.username ? 'field-error' : ''}
              />
              {fieldErrors.username && <span className="field-error-message">{fieldErrors.username}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.email ? 'field-error' : ''}
              />
              {fieldErrors.email && <span className="field-error-message">{fieldErrors.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.first_name ? 'field-error' : ''}
              />
              {fieldErrors.first_name && <span className="field-error-message">{fieldErrors.first_name}</span>}
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.last_name ? 'field-error' : ''}
              />
              {fieldErrors.last_name && <span className="field-error-message">{fieldErrors.last_name}</span>}
            </div>
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input 
              type="password" 
              name="password" 
              value={formData.password} 
              onChange={handleChange} 
              required 
              autoComplete="new-password" 
              id="player-password"
              data-lpignore="true"
              data-form-type="other"
              className={fieldErrors.password ? 'field-error' : ''}
            />
            {fieldErrors.password && <span className="field-error-message">{fieldErrors.password}</span>}
          </div>
          <div className="form-group">
            <label>WhatsApp Number *</label>
            <input 
              type="tel" 
              name="whatsapp_number" 
              value={formData.whatsapp_number} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              placeholder="e.g., +27 12 345 6789"
              className={fieldErrors.whatsapp_number ? 'field-error' : ''}
            />
            {fieldErrors.whatsapp_number && <span className="field-error-message">{fieldErrors.whatsapp_number}</span>}
            <small style={{ color: '#797b84', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              Enter your WhatsApp number or your parent's WhatsApp number
            </small>
          </div>
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: '#1a1b26', 
            border: '1px solid #383b48', 
            borderRadius: '5px',
            color: '#797b84',
            fontSize: '0.9rem'
          }}>
            <strong style={{ color: '#567ad8' }}>Note:</strong> Additional profile information (date of birth, address, parent details, etc.) will be completed by your coach or administrator after registration.
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <button onClick={onBackToLogin} className="back-btn">Back to Login</button>
      </div>
    </div>
  );
};

export default PlayerRegistrationPage;


